import { useEffect, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@skupreme/shared/ui';
import { useAuth } from '@contexts/auth';

type FooterType = 'info' | 'black' | 'green' | 'success';

/* The legacy theme's colours, kept so the bar looks the same. */
const BACKGROUNDS: Record<FooterType, string> = {
  info: 'sk:bg-primary',
  black: 'sk:bg-primary',
  green: 'sk:bg-brand',
  success: 'sk:bg-success-solid',
};

type FooterRowProps = HTMLAttributes<HTMLDivElement> & {
  type?: string;
};

/**
 * The bar fixed along the bottom of the page, right of the sidebar on wide
 * screens and full width below 769px. `mx-0!`
 * beats the unlayered Bootstrap `.row` margins FooterAlert's class brings.
 */
export function FooterRow({ type, className, ...rest }: FooterRowProps) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:fixed sk:bottom-0 sk:left-0 sk:z-[1010] sk:m-0 sk:mx-0! sk:w-full sk:py-[1.5em]',
        /* Beside the sidebar from 769px, as the legacy max-width: 768px rule had it. */
        'sk:min-[769px]:left-[240px] sk:min-[769px]:w-[calc(100%-240px)]',
        'sk:text-center sk:text-[15px] sk:leading-[18px] sk:text-white sk:transition-all sk:duration-200',
        BACKGROUNDS[type as FooterType] ?? BACKGROUNDS.success,
        className,
      )}
    />
  );
}

export function FooterHeader({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={cn('sk:text-xl sk:leading-[23px] sk:font-bold', className)}
    />
  );
}

export function FooterSubText({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...rest} className={cn('sk:text-xs sk:leading-[14px]', className)} />
  );
}

export function FooterContainer({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:flex sk:flex-1 sk:items-center sk:justify-center',
        className,
      )}
    />
  );
}

type FooterAlertProps = {
  type?: string;
  children?: ReactNode;
  className?: string;
  /** Accepted for the callers that pass it; the bar always clears the sidebar. */
  fullWidth?: boolean;
};

/** The bottom bar; while it shows, pages shorten themselves to clear it. */
export function FooterAlert({ type, children, className }: FooterAlertProps) {
  const { setFooterActive } = useAuth();

  useEffect(() => {
    setFooterActive(true);
    return () => setFooterActive(false);
  }, [setFooterActive]);

  return (
    <FooterRow
      type={type}
      className={cn('sk:flex sk:flex-wrap sk:-mx-4', className)}
    >
      {children}
    </FooterRow>
  );
}
